import { observer } from "mobx-react-lite";
import { useState } from "react";

import { IconButton, Spinner, Stack, ToolTipButton } from "@bps/fluent-ui";
import { DocumentEntityType } from "@libs/gateways/inbox/InboxGateway.dtos.ts";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { TreeViewSize } from "@shared-types/clinical/tree-view.enum.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { DocumentViewerDialog } from "@ui-components/document-viewer/DocumentViewerDialog.tsx";

import { AmendmentFormDialog } from "./amendment/AmendmentFormDialog.tsx";

interface PastVisitCommandBarProps {
  encounter: Encounter;
  clinicalRecord: ClinicalRecord;
  iconOnly?: boolean;
}
export const PastVisitCommandBar: React.FC<PastVisitCommandBarProps> = observer(
  ({ encounter, clinicalRecord }) => {
    const [isHidden, setIsHidden] = useState<boolean>(true);

    const { core, clinical, correspondence } = useStores();
    const { conditionConsultsDocumentId, setConditionConsultsDocumentId } =
      clinical.ui;

    const isAppendCompacted =
      clinical.ui.tabs.currentPatientRecordTab?.sidePanelSize !==
      TreeViewSize.Expanded;

    const { getInitialItems, onExportToPdfButtonClick } =
      usePatientRecordScreenContext();

    return (
      <Stack horizontal>
        <ToolTipButton
          compactModeProps={{
            showCompactMode: isAppendCompacted,
            toolTipContentOverride: "Append"
          }}
          buttonProps={{
            text: "Append",
            iconProps: { iconName: "EditNote" },
            styles: isAppendCompacted
              ? {
                  root: { width: "36px", height: "36px", border: 0, padding: 0 }
                }
              : { root: { border: 0, padding: 0 } },
            disabled: core.user?.id !== encounter.userId,
            onClick: () => {
              setIsHidden(false);
            }
          }}
        />
        {encounter.episodeOfCareId && (
          <DataFetcher
            fetch={() =>
              getInitialItems(encounter.patientId, encounter.episodeOfCareId!)
            }
            fallback={<Spinner />}
          >
            {encounters => (
              <IconButton
                menuIconProps={{ iconName: "More" }}
                menuProps={{
                  items: [
                    {
                      key: "copy",
                      text: "Print",
                      iconProps: {
                        iconName: "Print"
                      },
                      onClick: () => {
                        onExportToPdfButtonClick({
                          claimId: clinicalRecord.episodeOfCare?.claim?.id,
                          claimNumber:
                            clinicalRecord.episodeOfCare?.claim?.claimNumber,
                          diagnosis:
                            clinicalRecord.episodeOfCare?.primaryDiagnosis,
                          episodeOfCareId: encounter.episodeOfCareId!,
                          patientId: encounter.patientId,
                          encounters: encounters.filter(
                            x => x.id === encounter.id
                          )
                        });
                      }
                    }
                  ]
                }}
                styles={{
                  root: { width: "32px", height: "36px", padding: 0 },
                  flexContainer: { width: "32px", height: "36px" }
                }}
              />
            )}
          </DataFetcher>
        )}

        {conditionConsultsDocumentId && (
          <DocumentViewerDialog
            getDocument={async () => {
              const response = await correspondence.getDocumentUrl({
                patientId: encounter.patientId,
                documentId: conditionConsultsDocumentId
              });

              return {
                entityId: encounter.patientId,
                documentId: conditionConsultsDocumentId,
                previewUri: response.url,
                name: "Past consults & updates",
                extension: "pdf",
                entityType: DocumentEntityType.Patient
              };
            }}
            getDownloadUri={async () => {
              const documentUrlResponse = await correspondence.getDocumentUrl({
                patientId: encounter.patientId,
                documentId: conditionConsultsDocumentId
              });
              return documentUrlResponse.url;
            }}
            closeDocumentViewer={() =>
              setConditionConsultsDocumentId(undefined)
            }
          />
        )}
        <AmendmentFormDialog
          encounterId={encounter.id}
          isHidden={isHidden}
          setIsHidden={setIsHidden}
          clinicalRecord={clinicalRecord}
        />
      </Stack>
    );
  }
);
